import { useTranslation } from "react-i18next";
import styles from "./UserActionsTab.module.css";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import DefaultUserImage from "../../../assets/images/default-user.webp";

export default function UserActionsTab({
  allUsers,
  loadingUsers,
  usersError,
  fetchUsers,
  hasFetchedRef,
  authUser,
  selectedRoles,
  handleRoleSelect,
  handleRoleChange,
  handleToggleUserStatus,
}) {
  const { t } = useTranslation("common");

  const roleBadgeClass = (role) => {
    const r = String(role || "").toLowerCase();
    if (r === "owner") return styles.roleOwner;
    if (r === "admin") return styles.roleAdmin;
    return styles.roleUser;
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("userInfoPage.userActions.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("userInfoPage.userActions.description")}
          </p>
        </div>
        <button
          type="button"
          className={styles.headerActionButton}
          onClick={() => {
            hasFetchedRef.current = false;
            fetchUsers();
          }}
        >
          {t("userInfoPage.userActions.refreshUsers")}
        </button>
      </div>

      {loadingUsers && <LoadingSpinner />}

      {usersError && (
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("common.error")}: {usersError}
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      {!loadingUsers && !usersError && allUsers && allUsers.length > 0 && (
        <>
          <div
            className={`${styles.tableContainer} ${styles.tableContainerDesktopOnly}`}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("userInfoPage.userActions.tableHeaders.user")}</th>
                  <th>{t("userInfoPage.userActions.tableHeaders.email")}</th>
                  <th>
                    {t("userInfoPage.userActions.tableHeaders.currentRole")}
                  </th>
                  {authUser?.role === "owner" && (
                    <th>
                      {t("userInfoPage.userActions.tableHeaders.changeRole")}
                    </th>
                  )}
                  <th>{t("userInfoPage.userActions.tableHeaders.account")}</th>
                  <th>{t("userInfoPage.userActions.tableHeaders.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => (
                  <tr key={user._id}>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={
                                user.name ||
                                t("userInfoPage.userActions.roles.user")
                              }
                              className={styles.profileImage}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DefaultUserImage;
                              }}
                            />
                          ) : (
                            (user.name || "U").charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className={styles.userName}>
                          {user.name || t("userInfoPage.userActions.noName")}
                        </div>
                      </div>
                    </td>
                    <td className={styles.emailCell}>{user.email}</td>
                    <td className={styles.roleCell}>
                      <span
                        className={`${styles.roleBadge} ${roleBadgeClass(user.role)}`}
                      >
                        {user.role === "owner" ? (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                <g
                                  id="🔍-Product-Icons"
                                  stroke="none"
                                  strokeWidth="1"
                                  fill="none"
                                  fillRule="evenodd"
                                >
                                  <g
                                    id="ic_fluent_owner_24_filled"
                                    fill="currentColor"
                                    fillRule="nonzero"
                                  >
                                    <path
                                      d="M17.0016031,15.2440856 L17.0009052,21.2451182 C17.0009052,21.8527788 16.3161092,22.2081862 15.8192057,21.8584172 L12.0007623,19.1706254 L8.18435794,21.8583162 C7.68747081,22.2082475 7.00251516,21.8528589 7.00251516,21.2451182 L7.00069412,15.2459273 C8.37018531,16.3435035 10.1084262,17 12,17 C13.8926316,17 15.6317588,16.3427691 17.0016031,15.2440856 Z M12,2 C15.8659932,2 19,5.13400675 19,9 C19,12.8659932 15.8659932,16 12,16 C8.13400675,16 5,12.8659932 5,9 C5,5.13400675 8.13400675,2 12,2 Z"
                                      id="🎨-Color"
                                    ></path>
                                  </g>
                                </g>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.owner")}
                          </>
                        ) : user.role === "admin" ? (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M12 14v8H4a8 8 0 0 1 8-8zm0-1c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm9 4h1v5h-8v-5h1v-1a3 3 0 0 1 6 0v1zm-2 0v-1a1 1 0 0 0-2 0v1h2z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.admin")}
                          </>
                        ) : (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M2 22a8 8 0 1 1 16 0H2zm8-9c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm7.363 2.233A7.505 7.505 0 0 1 22.983 22H20c0-2.61-1-4.986-2.637-6.767zm-2.023-2.276A7.98 7.98 0 0 0 18 7a7.964 7.964 0 0 0-1.015-3.903A5 5 0 0 1 21 8a4.999 4.999 0 0 1-5.66 4.957z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.user")}
                          </>
                        )}
                      </span>
                    </td>
                    {authUser?.role === "owner" && (
                      <td className={styles.changeRoleCell}>
                        <div className={styles.roleChangeContainer}>
                          <div className={styles.roleDropdownWrapper}>
                            <select
                              className={styles.roleSelect}
                              value={selectedRoles[user._id] || user.role}
                              onChange={(e) =>
                                handleRoleSelect(user._id, e.target.value)
                              }
                            >
                              <option value={user.role} disabled>
                                {user.role === "owner"
                                  ? t("userInfoPage.userActions.roles.owner")
                                  : user.role === "admin"
                                  ? t("userInfoPage.userActions.roles.admin")
                                  : t(
                                      "userInfoPage.userActions.roles.user"
                                    )}{" "}
                                {t("userInfoPage.userActions.actions.current")}
                              </option>
                              {user.role !== "admin" && (
                                <option value="admin">
                                  {t("userInfoPage.userActions.roles.admin")}
                                </option>
                              )}
                              {user.role !== "user" && (
                                <option value="user">
                                  {t("userInfoPage.userActions.roles.user")}
                                </option>
                              )}
                            </select>
                          </div>
                          {user.role !== "owner" &&
                            selectedRoles[user._id] &&
                            selectedRoles[user._id] !== user.role && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRoleChange(
                                    user._id,
                                    selectedRoles[user._id]
                                  )
                                }
                                className={styles.updateRoleButton}
                              >
                                {t("userInfoPage.userActions.actions.update")}
                              </button>
                            )}
                        </div>
                      </td>
                    )}
                    <td className={styles.statusCell}>
                      <span
                        className={`${styles.statusText} ${
                          user.isActive
                            ? styles.statusTextActive
                            : styles.statusTextInactive
                        }`}
                      >
                        {user.isActive
                          ? t("userInfoPage.userActions.status.active")
                          : t("userInfoPage.userActions.status.inactive")}
                      </span>
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionButtons}>
                        {(authUser?.role === "owner" ||
                          (authUser?.role === "admin" &&
                            user.role !== "owner")) && (
                          <button
                            type="button"
                            onClick={() =>
                              handleToggleUserStatus(user._id, !user.isActive)
                            }
                            className={`${styles.toggleStatusButton} ${
                              user.isActive
                                ? styles.toggleStatusButtonDanger
                                : styles.toggleStatusButtonSuccess
                            }`}
                          >
                            {user.isActive
                              ? t("userInfoPage.userActions.actions.deactivate")
                              : t("userInfoPage.userActions.actions.activate")}
                          </button>
                        )}
                        {authUser?.role === "admin" &&
                          user.role === "owner" && (
                            <span className={styles.disabledActionText}>
                              {t(
                                "userInfoPage.userActions.actions.cannotModifyOwner"
                              )}
                            </span>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileCardLayout}>
            <div className={styles.usersGrid}>
              {allUsers.map((user) => (
                <div key={user._id} className={styles.userCard}>
                  <div className={styles.userHeader}>
                    <div className={styles.userInfo}>
                      <div className={styles.userAvatar}>
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt={
                              user.name ||
                              t("userInfoPage.userActions.roles.user")
                            }
                            className={styles.profileImage}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DefaultUserImage;
                            }}
                          />
                        ) : (
                          (user.name || "U").charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>
                          {user.name || t("userInfoPage.userActions.noName")}
                        </div>
                        <div className={styles.userEmail}>{user.email}</div>
                      </div>
                    </div>
                    <span
                      className={`${styles.statusBadge} ${
                        user.isActive
                          ? styles.statusBadgeActive
                          : styles.statusBadgeInactive
                      }`}
                    >
                      {user.isActive
                        ? t("userInfoPage.userActions.status.active")
                        : t("userInfoPage.userActions.status.inactive")}
                    </span>
                  </div>

                  <div className={styles.userMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.userActions.labels.role")}
                      </span>
                      <span
                        className={`${styles.roleBadge} ${roleBadgeClass(user.role)}`}
                      >
                        {user.role === "owner" ? (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                <g
                                  id="🔍-Product-Icons"
                                  stroke="none"
                                  strokeWidth="1"
                                  fill="none"
                                  fillRule="evenodd"
                                >
                                  <g
                                    id="ic_fluent_owner_24_filled"
                                    fill="currentColor"
                                    fillRule="nonzero"
                                  >
                                    <path
                                      d="M17.0016031,15.2440856 L17.0009052,21.2451182 C17.0009052,21.8527788 16.3161092,22.2081862 15.8192057,21.8584172 L12.0007623,19.1706254 L8.18435794,21.8583162 C7.68747081,22.2082475 7.00251516,21.8528589 7.00251516,21.2451182 L7.00069412,15.2459273 C8.37018531,16.3435035 10.1084262,17 12,17 C13.8926316,17 15.6317588,16.3427691 17.0016031,15.2440856 Z M12,2 C15.8659932,2 19,5.13400675 19,9 C19,12.8659932 15.8659932,16 12,16 C8.13400675,16 5,12.8659932 5,9 C5,5.13400675 8.13400675,2 12,2 Z"
                                      id="🎨-Color"
                                    ></path>
                                  </g>
                                </g>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.owner")}
                          </>
                        ) : user.role === "admin" ? (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M12 14v8H4a8 8 0 0 1 8-8zm0-1c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm9 4h1v5h-8v-5h1v-1a3 3 0 0 1 6 0v1zm-2 0v-1a1 1 0 0 0-2 0v1h2z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.admin")}
                          </>
                        ) : (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M2 22a8 8 0 1 1 16 0H2zm8-9c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm7.363 2.233A7.505 7.505 0 0 1 22.983 22H20c0-2.61-1-4.986-2.637-6.767zm-2.023-2.276A7.98 7.98 0 0 0 18 7a7.964 7.964 0 0 0-1.015-3.903A5 5 0 0 1 21 8a4.999 4.999 0 0 1-5.66 4.957z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.userActions.roles.user")}
                          </>
                        )}
                      </span>
                    </div>

                    {authUser?.role === "owner" && (
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>
                          {t("userInfoPage.userActions.labels.changeRole")}
                        </span>
                        <div className={styles.roleChangeContainer}>
                          <div className={styles.roleDropdownWrapper}>
                            <select
                              className={styles.roleSelect}
                              value={selectedRoles[user._id] || user.role}
                              onChange={(e) =>
                                handleRoleSelect(user._id, e.target.value)
                              }
                            >
                              <option value={user.role} disabled>
                                {user.role === "owner"
                                  ? t("userInfoPage.userActions.roles.owner")
                                  : user.role === "admin"
                                  ? t("userInfoPage.userActions.roles.admin")
                                  : t(
                                      "userInfoPage.userActions.roles.user"
                                    )}{" "}
                                {t("userInfoPage.userActions.actions.current")}
                              </option>
                              {user.role !== "admin" && (
                                <option value="admin">
                                  {t("userInfoPage.userActions.roles.admin")}
                                </option>
                              )}
                              {user.role !== "user" && (
                                <option value="user">
                                  {t("userInfoPage.userActions.roles.user")}
                                </option>
                              )}
                            </select>
                          </div>
                          {user.role !== "owner" &&
                            selectedRoles[user._id] &&
                            selectedRoles[user._id] !== user.role && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRoleChange(
                                    user._id,
                                    selectedRoles[user._id]
                                  )
                                }
                                className={styles.updateRoleButton}
                              >
                                {t("userInfoPage.userActions.actions.update")}
                              </button>
                            )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={styles.cardActions}>
                    <div className={styles.actionButtons}>
                      {(authUser?.role === "owner" ||
                        (authUser?.role === "admin" &&
                          user.role !== "owner")) && (
                        <button
                          type="button"
                          onClick={() =>
                            handleToggleUserStatus(user._id, !user.isActive)
                          }
                          className={`${styles.toggleStatusButton} ${
                            styles.toggleStatusButtonFullWidth
                          } ${
                            user.isActive
                              ? styles.toggleStatusButtonDanger
                              : styles.toggleStatusButtonSuccess
                          }`}
                        >
                          {user.isActive
                            ? t("userInfoPage.userActions.actions.deactivate")
                            : t("userInfoPage.userActions.actions.activate")}
                        </button>
                      )}
                      {authUser?.role === "admin" && user.role === "owner" && (
                        <span className={styles.disabledActionText}>
                          {t(
                            "userInfoPage.userActions.actions.cannotModifyOwner"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!loadingUsers && !usersError && (!allUsers || allUsers.length === 0) && (
        <div className={styles.noUsers}>
          <p>{t("userInfoPage.userActions.noUsers")}</p>
          <button
            type="button"
            className={styles.noUsersButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("userInfoPage.userActions.refreshUsers")}
          </button>
        </div>
      )}
    </div>
  );
}
